import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Paragraph,
} from '@app/shared/ui/atoms/Common';
import { CountSelect, SummaryRow } from '@app/shared/ui/molecules/ProfitCalculator';
import { telegramVibrate, cn } from '@app/shared/utils/common';
import { calcProfitIncome } from '@app/shared/utils/calcProfitIncome';
import { diamondLightIcon } from '@app/assets';
import { Settlement } from '@app/store/slices/settlements/settlementsTypes';
import { useLazyGetTonPriceQuery } from '@app/store/slices/tonPrice/tonPriceApi';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';
import { Tutorial } from '@app/shared/ui/organisms/Common/Tutorial';

interface ProfitCalculatorProps {
  settlements?: Settlement[];
}

export function ProfitCalculator({ settlements }: ProfitCalculatorProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const tutorial = useAppSelector((state) => state.tutorial);
  const [userSelectValue, setUserSelectValue] = useState<string | undefined>();
  const [settlementsAmount, setSettlementsAmount] = useState<string | undefined>();
  const [income, setIncome] = useState(0);

  const [getTonPrice, { data: tonPrice }] = useLazyGetTonPriceQuery();

  const isTutorial = tutorial.step === 'CALC_1' || tutorial.step === 'CALC_2';

  useEffect(() => {
    void getTonPrice();
  }, []);

  useEffect(() => {
    if (tutorial.step === 'CALC_1') {
      setUserSelectValue('4');
      setSettlementsAmount('10');
    }
    if (tutorial.step === 'CALC_2') {
      setUserSelectValue('4');
      setSettlementsAmount('16');
    }
  }, [tutorial]);

  const handleCalculateBtnClick = () => {
    telegramVibrate();

    if (isTutorial) {
      dispatch(tutorialActions.nextCalcStep());
    }

    const peopleCount = Number(userSelectValue);
    const settlementsCount = Number(settlementsAmount);

    const selectedSettlements = settlements?.slice(0, settlementsCount);
    const settlementPrices = selectedSettlements?.map((s) => s.price);

    if (peopleCount && settlementsCount && settlementPrices) {
      const totalIncome = calcProfitIncome(settlementPrices, peopleCount);

      setIncome(totalIncome);
    }
  };

  return (
    <Tutorial
      key={tutorial.step}
      text={
        tutorial.step === 'CALC_1'
          ? t('tutorial.calculator.2_step')
          : tutorial.step === 'CALC_2'
            ? t('tutorial.calculator.3_step')
            : ''
      }
      enabled={isTutorial}
      shouldPulse={false}
    >
      <Card>
        <CardHeader className="rounded-t-lg bg-blue-primary p-[14px_0]">
          <CardTitle className="text-center text-xl font-bold text-foreground">
            {t('profit_calc.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-[28px] pt-6">
          <CountSelect
            disabled={isTutorial}
            value={userSelectValue}
            onChange={setUserSelectValue}
            length={10}
            label={t('profit_calc.people_select_label')}
            itemKey="profit_calc.user"
          />

          <CountSelect
            disabled={isTutorial}
            value={settlementsAmount}
            onChange={setSettlementsAmount}
            length={8}
            start={2}
            step={2}
            label={t('profit_calc.settlements_amount_label')}
            itemKey="profit_calc.settlements"
          />

          <Button
            shadow="cyan"
            onClick={handleCalculateBtnClick}
            disabled={!userSelectValue || !settlementsAmount}
            className={cn(
              'disabled:cursor-not-allowed disabled:bg-accent/50 disabled:shadow-none',
              isTutorial && 'animate-pulse'
            )}
          >
            {t('profit_calc.calc_button')}
          </Button>
        </CardContent>

        <CardFooter className="flex justify-center gap-1 px-1">
          <SummaryRow icon={diamondLightIcon} value={income} unit="TON" />
          {!!tonPrice && !!income && (
            <Paragraph size="lg">&asymp; {(income * tonPrice).toFixed()} USD</Paragraph>
          )}
          {/*TODO: delete if not necessary*/}
          {/*<SummaryRow icon={userCircleIcon} value={0} unit={t('profit_calc.levels', { level: 6 })} />*/}
        </CardFooter>
      </Card>
    </Tutorial>
  );
}
